import React, { useEffect, useState } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    BarChart,
    Bar,
} from "recharts";
import { adminApi } from "@/lib/adminApi";
import LiveSessions from "./LiveSessions";

const STAT_WINDOWS = [
    { d: 7, label: "7 Tage" },
    { d: 30, label: "30 Tage" },
    { d: 90, label: "90 Tage" },
    { d: 365, label: "12 Monate" },
];

const MODES = [
    { v: "all", label: "Alle" },
    { v: "real", label: "Echte Notfälle" },
    { v: "demo", label: "Demo" },
];

const FUNNEL_LABELS = {
    demo_started: "Demo gestartet",
    emergency_pressed: "Notfall-Knopf",
    called_112: "Notruf gewählt",
    cpr_started: "Reanimation gestartet",
};

function StatCard({ label, value, sub, testid, accent }) {
    return (
        <div
            className={`bg-white border ${
                accent ? "border-emerald-200" : "border-zinc-200"
            } rounded-2xl p-5 shadow-sm`}
            data-testid={testid}
        >
            <div className="text-xs uppercase tracking-widest text-zinc-500">
                {label}
            </div>
            <div className="text-3xl font-display font-bold text-zinc-900 mt-2">
                {value}
            </div>
            {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
        </div>
    );
}

function Card({ title, children, testid }) {
    return (
        <section
            className="bg-white border border-zinc-200 rounded-2xl shadow-sm p-4 sm:p-6"
            data-testid={testid}
        >
            <h2 className="text-sm font-semibold text-zinc-600 mb-3">{title}</h2>
            {children}
        </section>
    );
}

export default function AdminDashboard({ onLogout }) {
    const [days, setDays] = useState(30);
    const [mode, setMode] = useState("all");
    const [stats, setStats] = useState(null);
    const [emails, setEmails] = useState(null);
    const [stickers, setStickers] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = async (d, m) => {
        setLoading(true);
        setErr("");
        try {
            const [s, e, st] = await Promise.all([
                adminApi.stats(d, m),
                adminApi.emails(500),
                adminApi.stickerRequests(500),
            ]);
            setStats(s);
            setEmails(e);
            setStickers(st);
        } catch (ex) {
            if (ex.message === "UNAUTHORIZED") {
                onLogout();
                return;
            }
            setErr("Daten konnten nicht geladen werden.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // eslint-disable-next-line
        load(days, mode);
    }, [days, mode]);

    const downloadCsv = async () => {
        try {
            const r = await adminApi.authedFetch("/api/admin/emails/export");
            if (!r.ok) throw new Error("download failed");
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `safetyassist-emails-${new Date()
                .toISOString()
                .slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            setErr("CSV-Export fehlgeschlagen.");
        }
    };

    const handleLogout = () => {
        adminApi.clearToken();
        onLogout();
    };

    const funnelData =
        stats?.funnel?.map((f) => ({
            ...f,
            label: FUNNEL_LABELS[f.event] || f.event,
        })) || [];

    const demoRealData = stats?.demoReal
        ? [
              { mode: "Echte Notfälle", views: stats.demoReal.real?.views || 0, visitors: stats.demoReal.real?.visitors || 0 },
              { mode: "Demo", views: stats.demoReal.demo?.views || 0, visitors: stats.demoReal.demo?.visitors || 0 },
          ]
        : [];

    return (
        <div className="min-h-screen bg-zinc-50">
            <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-zinc-500">
                            SafetyAssist
                        </div>
                        <h1 className="text-xl sm:text-2xl font-display font-bold text-zinc-900">
                            Admin Dashboard
                        </h1>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div
                            className="inline-flex rounded-lg border border-zinc-300 overflow-hidden bg-white"
                            data-testid="admin-mode-toggle"
                        >
                            {MODES.map((m) => (
                                <button
                                    key={m.v}
                                    onClick={() => setMode(m.v)}
                                    data-testid={`admin-mode-${m.v}`}
                                    className={`px-3 py-2 text-sm ${
                                        mode === m.v
                                            ? "bg-zinc-900 text-white"
                                            : "text-zinc-700 hover:bg-zinc-50"
                                    }`}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>
                        <select
                            value={days}
                            onChange={(e) =>
                                setDays(parseInt(e.target.value, 10))
                            }
                            data-testid="admin-window-select"
                            className="px-3 py-2 border border-zinc-300 rounded-lg text-sm bg-white"
                        >
                            {STAT_WINDOWS.map((w) => (
                                <option key={w.d} value={w.d}>
                                    {w.label}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={handleLogout}
                            data-testid="admin-logout-btn"
                            className="px-3 py-2 border border-zinc-300 hover:bg-zinc-50 rounded-lg text-sm text-zinc-700"
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
                {err && (
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                        {err}
                    </div>
                )}

                {loading && (
                    <div className="text-zinc-500 text-sm">Lade Daten…</div>
                )}

                {stats && (
                    <>
                        <div
                            className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
                            data-testid="admin-stat-cards"
                        >
                            <StatCard
                                label="Seitenaufrufe"
                                value={stats.totals.views.toLocaleString("de-DE")}
                                sub={`letzte ${days} Tage`}
                                testid="stat-card-views"
                            />
                            <StatCard
                                label="Eindeutige Besucher"
                                value={stats.totals.uniqueVisitors.toLocaleString(
                                    "de-DE",
                                )}
                                sub={`letzte ${days} Tage`}
                                testid="stat-card-visitors"
                            />
                            <StatCard
                                label="Ø Verweildauer"
                                value={`${stats.totals.avgSessionSec}s`}
                                sub={`Median ${stats.totals.medianSessionSec}s · ${stats.totals.sessions} Sessions`}
                                testid="stat-card-duration"
                            />
                            <StatCard
                                label="Reanimation erreicht"
                                value={stats.totals.reachedCpr.toLocaleString(
                                    "de-DE",
                                )}
                                sub={`Notruf: ${stats.totals.called112}`}
                                testid="stat-card-cpr"
                                accent
                            />
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                            <StatCard
                                label="E-Mails gesamt"
                                value={stats.totals.emails.toLocaleString(
                                    "de-DE",
                                )}
                                sub="seit Start"
                                testid="stat-card-emails-total"
                            />
                            <StatCard
                                label="Neue E-Mails"
                                value={stats.totals.emailsInWindow.toLocaleString(
                                    "de-DE",
                                )}
                                sub={`letzte ${days} Tage`}
                                testid="stat-card-emails-window"
                            />
                            {mode === "all" && stats.demoReal && (
                                <>
                                    <StatCard
                                        label="Echte Notfälle"
                                        value={stats.demoReal.real?.visitors || 0}
                                        sub={`${stats.demoReal.real?.views || 0} Aufrufe`}
                                        testid="stat-card-real"
                                    />
                                    <StatCard
                                        label="Demo-Nutzer"
                                        value={stats.demoReal.demo?.visitors || 0}
                                        sub={`${stats.demoReal.demo?.views || 0} Aufrufe`}
                                        testid="stat-card-demo"
                                    />
                                </>
                            )}
                        </div>

                        <Card title="Aufrufe pro Tag" testid="admin-chart-per-day">
                            <div className="h-60 sm:h-72">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={stats.perDay}>
                                        <CartesianGrid
                                            stroke="#e4e4e7"
                                            strokeDasharray="3 3"
                                        />
                                        <XAxis
                                            dataKey="day"
                                            tick={{
                                                fontSize: 11,
                                                fill: "#71717a",
                                            }}
                                            tickFormatter={(v) =>
                                                v ? v.slice(5) : ""
                                            }
                                        />
                                        <YAxis
                                            tick={{
                                                fontSize: 11,
                                                fill: "#71717a",
                                            }}
                                        />
                                        <Tooltip />
                                        <Line
                                            type="monotone"
                                            dataKey="views"
                                            stroke="#245288"
                                            strokeWidth={2}
                                            dot={false}
                                            name="Aufrufe"
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="visitors"
                                            stroke="#A72920"
                                            strokeWidth={2}
                                            dot={false}
                                            name="Besucher"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>

                        <Card
                            title="Wichtige Schritte (eindeutige Besucher)"
                            testid="admin-chart-funnel"
                        >
                            <div className="h-60">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={funnelData}
                                        layout="vertical"
                                        margin={{ left: 20 }}
                                    >
                                        <CartesianGrid
                                            stroke="#e4e4e7"
                                            strokeDasharray="3 3"
                                        />
                                        <XAxis
                                            type="number"
                                            tick={{
                                                fontSize: 11,
                                                fill: "#71717a",
                                            }}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="label"
                                            width={170}
                                            tick={{
                                                fontSize: 12,
                                                fill: "#27272a",
                                            }}
                                        />
                                        <Tooltip />
                                        <Bar
                                            dataKey="visitors"
                                            fill="#A72920"
                                            name="Besucher"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>

                        <div className="grid lg:grid-cols-2 gap-4">
                            <Card
                                title="Verweildauer (Sessions)"
                                testid="admin-chart-duration"
                            >
                                <div className="h-60">
                                    <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                    >
                                        <BarChart data={stats.sessionHistogram}>
                                            <CartesianGrid
                                                stroke="#e4e4e7"
                                                strokeDasharray="3 3"
                                            />
                                            <XAxis
                                                dataKey="bucket"
                                                tick={{
                                                    fontSize: 11,
                                                    fill: "#71717a",
                                                }}
                                            />
                                            <YAxis
                                                tick={{
                                                    fontSize: 11,
                                                    fill: "#71717a",
                                                }}
                                            />
                                            <Tooltip />
                                            <Bar
                                                dataKey="sessions"
                                                fill="#245288"
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>

                            <Card
                                title="Top Länder/Regionen"
                                testid="admin-chart-countries"
                            >
                                <div className="h-60">
                                    <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                    >
                                        <BarChart
                                            data={stats.perCountry.slice(0, 12)}
                                        >
                                            <CartesianGrid
                                                stroke="#e4e4e7"
                                                strokeDasharray="3 3"
                                            />
                                            <XAxis
                                                dataKey="country"
                                                tick={{
                                                    fontSize: 11,
                                                    fill: "#71717a",
                                                }}
                                            />
                                            <YAxis
                                                tick={{
                                                    fontSize: 11,
                                                    fill: "#71717a",
                                                }}
                                            />
                                            <Tooltip />
                                            <Bar
                                                dataKey="views"
                                                fill="#0F8040"
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>
                        </div>

                        <div className="grid lg:grid-cols-2 gap-4">
                            <Card
                                title="Top Sprachen"
                                testid="admin-chart-locales"
                            >
                                <div className="h-60">
                                    <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                    >
                                        <BarChart
                                            data={stats.perLocale.slice(0, 10)}
                                        >
                                            <CartesianGrid
                                                stroke="#e4e4e7"
                                                strokeDasharray="3 3"
                                            />
                                            <XAxis
                                                dataKey="locale"
                                                tick={{
                                                    fontSize: 11,
                                                    fill: "#71717a",
                                                }}
                                            />
                                            <YAxis
                                                tick={{
                                                    fontSize: 11,
                                                    fill: "#71717a",
                                                }}
                                            />
                                            <Tooltip />
                                            <Bar
                                                dataKey="views"
                                                fill="#245288"
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>

                            <Card
                                title="Top Schritte"
                                testid="admin-chart-pages"
                            >
                                <div className="h-60">
                                    <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                    >
                                        <BarChart
                                            data={stats.perPage.slice(0, 10)}
                                        >
                                            <CartesianGrid
                                                stroke="#e4e4e7"
                                                strokeDasharray="3 3"
                                            />
                                            <XAxis
                                                dataKey="page"
                                                tick={{
                                                    fontSize: 11,
                                                    fill: "#71717a",
                                                }}
                                            />
                                            <YAxis
                                                tick={{
                                                    fontSize: 11,
                                                    fill: "#71717a",
                                                }}
                                            />
                                            <Tooltip />
                                            <Bar
                                                dataKey="views"
                                                fill="#A72920"
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>
                        </div>

                        {mode === "all" && demoRealData.length > 0 && (
                            <Card
                                title="Demo vs. Echte Notfälle"
                                testid="admin-chart-demoreal"
                            >
                                <div className="h-60">
                                    <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                    >
                                        <BarChart data={demoRealData}>
                                            <CartesianGrid
                                                stroke="#e4e4e7"
                                                strokeDasharray="3 3"
                                            />
                                            <XAxis
                                                dataKey="mode"
                                                tick={{
                                                    fontSize: 12,
                                                    fill: "#27272a",
                                                }}
                                            />
                                            <YAxis
                                                tick={{
                                                    fontSize: 11,
                                                    fill: "#71717a",
                                                }}
                                            />
                                            <Tooltip />
                                            <Bar
                                                dataKey="views"
                                                fill="#245288"
                                                name="Aufrufe"
                                            />
                                            <Bar
                                                dataKey="visitors"
                                                fill="#A72920"
                                                name="Besucher"
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>
                        )}
                    </>
                )}

                {/* Live Sessions table (24h window, last 7 days selectable) */}
                <LiveSessions mode={mode} />

                {emails && (
                    <section
                        className="bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden"
                        data-testid="admin-emails-section"
                    >
                        <div className="px-4 sm:px-6 py-3 flex items-center justify-between border-b border-zinc-200 gap-3 flex-wrap">
                            <div>
                                <h2 className="text-sm font-semibold text-zinc-600">
                                    E-Mail-Anmeldungen
                                </h2>
                                <div className="text-xs text-zinc-500">
                                    {emails.total} gesamt · zeige neueste{" "}
                                    {emails.items.length}
                                </div>
                            </div>
                            <button
                                onClick={downloadCsv}
                                data-testid="admin-export-csv"
                                className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg"
                            >
                                CSV exportieren
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-zinc-50 text-zinc-600">
                                    <tr>
                                        <th className="text-left px-4 py-2 font-medium">
                                            E-Mail
                                        </th>
                                        <th className="text-left px-4 py-2 font-medium">
                                            Sprache
                                        </th>
                                        <th className="text-left px-4 py-2 font-medium">
                                            Quelle
                                        </th>
                                        <th className="text-left px-4 py-2 font-medium">
                                            Angemeldet
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {emails.items.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={4}
                                                className="px-4 py-6 text-center text-zinc-500"
                                            >
                                                Noch keine Anmeldungen.
                                            </td>
                                        </tr>
                                    )}
                                    {emails.items.map((r, i) => (
                                        <tr
                                            key={r.email + i}
                                            className="border-t border-zinc-100"
                                            data-testid={`admin-email-row-${i}`}
                                        >
                                            <td className="px-4 py-2 font-mono text-zinc-900">
                                                {r.email}
                                            </td>
                                            <td className="px-4 py-2 text-zinc-700">
                                                {r.locale || "—"}
                                            </td>
                                            <td className="px-4 py-2 text-zinc-700">
                                                {r.source || "—"}
                                            </td>
                                            <td className="px-4 py-2 text-zinc-500">
                                                {r.createdAt
                                                    ? new Date(
                                                          r.createdAt,
                                                      ).toLocaleString("de-DE")
                                                    : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}

                {stickers && (
                    <section
                        className="bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden"
                        data-testid="admin-stickers-section"
                    >
                        <div className="px-4 sm:px-6 py-3 flex items-center justify-between border-b border-zinc-200 gap-3 flex-wrap">
                            <div>
                                <h2 className="text-sm font-semibold text-zinc-600">
                                    Aufkleber-Anfragen
                                </h2>
                                <div className="text-xs text-zinc-500">
                                    {stickers.total} gesamt · zeige neueste{" "}
                                    {stickers.items.length}
                                </div>
                            </div>
                            <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/sticker-requests/export`}
                                onClick={async (e) => {
                                    e.preventDefault();
                                    try {
                                        const r = await adminApi.authedFetch(
                                            "/api/admin/sticker-requests/export",
                                        );
                                        if (!r.ok) return;
                                        const blob = await r.blob();
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement("a");
                                        a.href = url;
                                        a.download = `safetyassist-sticker-requests-${new Date()
                                            .toISOString()
                                            .slice(0, 10)}.csv`;
                                        document.body.appendChild(a);
                                        a.click();
                                        a.remove();
                                        URL.revokeObjectURL(url);
                                    } catch {
                                        setErr("CSV-Export fehlgeschlagen.");
                                    }
                                }}
                                data-testid="admin-stickers-export"
                                className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg"
                            >
                                CSV exportieren
                            </a>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-zinc-50 text-zinc-600">
                                    <tr>
                                        <th className="text-left px-4 py-2 font-medium">
                                            Datum
                                        </th>
                                        <th className="text-left px-4 py-2 font-medium">
                                            Firma
                                        </th>
                                        <th className="text-left px-4 py-2 font-medium">
                                            Ansprechpartner
                                        </th>
                                        <th className="text-left px-4 py-2 font-medium">
                                            E-Mail
                                        </th>
                                        <th className="text-left px-4 py-2 font-medium">
                                            Adresse
                                        </th>
                                        <th className="text-left px-4 py-2 font-medium">
                                            Anzahl
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stickers.items.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={6}
                                                className="px-4 py-6 text-center text-zinc-500"
                                            >
                                                Noch keine Anfragen.
                                            </td>
                                        </tr>
                                    )}
                                    {stickers.items.map((r, i) => (
                                        <tr
                                            key={r.email + i}
                                            className="border-t border-zinc-100"
                                            data-testid={`admin-sticker-row-${i}`}
                                        >
                                            <td className="px-4 py-2 text-zinc-500 whitespace-nowrap">
                                                {r.createdAt
                                                    ? new Date(
                                                          r.createdAt,
                                                      ).toLocaleString("de-DE")
                                                    : "—"}
                                            </td>
                                            <td className="px-4 py-2 font-medium text-zinc-900">
                                                {r.companyName}
                                            </td>
                                            <td className="px-4 py-2 text-zinc-700">
                                                {r.contactName || "—"}
                                            </td>
                                            <td className="px-4 py-2 text-zinc-700 font-mono">
                                                {r.email}
                                            </td>
                                            <td className="px-4 py-2 text-zinc-700">
                                                {r.street}, {r.zip} {r.city}
                                                {r.country && r.country !== "Deutschland"
                                                    ? `, ${r.country}`
                                                    : ""}
                                            </td>
                                            <td className="px-4 py-2 text-zinc-700">
                                                {r.quantity || "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}
            </main>
        </div>
    );
}
